import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Building2,
  Target,
  FileText,
  Shield,
  DollarSign,
  BarChart3,
  Upload,
  Plus,
  X,
  Info,
  AlertCircle,
  Sparkles,
  Calendar,
  Users,
  Link,
  Image,
  Video,
  MessageSquare
} from 'lucide-react'
import { useAuth } from '../../App'

const steps = [
  { id: 1, name: 'Brand Overview', icon: Building2 },
  { id: 2, name: 'Campaign Objectives', icon: Target },
  { id: 3, name: 'Timeline & Creators', icon: Calendar },
  { id: 4, name: 'Deliverables', icon: FileText },
  { id: 5, name: 'Compliance', icon: Shield },
  { id: 6, name: 'Budget & Terms', icon: DollarSign },
  { id: 7, name: 'Measurement', icon: BarChart3 },
]

const platforms = ['Instagram', 'TikTok', 'YouTube', 'LinkedIn', 'Twitter/X']
const deliverableTypes = ['Reel/Short', 'Stories', 'Carousel Post', 'Single Post', 'Long-form Video', 'Newsletter', 'Live Stream', 'Podcast Mention']
const goals = ['Awareness', 'Engagement', 'Education', 'Conversion', 'Community Building', 'Product Launch', 'Brand Repositioning', 'Lead Generation']
const tones = ['Educational', 'Conversational', 'Clinical/Professional', 'Inspirational', 'Playful', 'Empathetic', 'Authoritative']
const creatorTiers = ['Flagship (500K+)', 'Macro (100K-500K)', 'Mid-Tier (25K-100K)', 'Micro (5K-25K)', 'Nano (1K-5K)']
const creatorSpecialties = ['Physician', 'Nurse/NP', 'Pharmacist', 'Mental Health', 'Nutritionist', 'Physical Therapist', 'Patient Advocate', 'Medical Student', 'Health Coach', 'Researcher']
const contentFormats = ['Talking Head', 'Demonstration', 'Day-in-the-Life', 'Before/After', 'Interview/Q&A', 'Tutorial', 'Reaction/Review', 'Trending Audio', 'Green Screen']

function BrandBriefBuilder() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [currentStep, setCurrentStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [formData, setFormData] = useState({
    // Brand Overview (Step 1)
    brandStory: '',
    brandValues: [],
    toneOfVoice: '',
    targetAudience: '',
    brandWebsite: '',
    productServiceName: '',
    productDescription: '',
    uniqueSellingPoints: [''],
    competitorInfo: '',
    brandAssets: '',

    // Campaign Objectives (Step 2)
    campaignTitle: '',
    campaignDescription: '',
    primaryGoal: '',
    secondaryGoal: '',
    callToAction: '',
    ctaLink: '',
    coreMessage: '',
    talkingPoints: [''],
    keyBenefits: [''],
    proofPoints: [''],

    // Timeline & Creator Requirements (Step 3)
    campaignStartDate: '',
    campaignEndDate: '',
    contentDeadline: '',
    postingWindow: '',
    preferredCreatorTiers: [],
    preferredSpecialties: [],
    creatorPersonality: '',
    exclusivityRequired: false,
    exclusivityDuration: '',
    geographicRequirements: '',

    // Deliverables (Step 4)
    platforms: [],
    deliverableTypes: [],
    contentFormats: [],
    creativeDirection: '',
    videoLength: '',
    captionGuidelines: '',
    hashtagRequirements: [''],
    mentionRequirements: [''],
    mandatoryInclusions: [''],
    restrictions: [''],
    referenceLinks: [''],
    moodDescriptors: '',

    // Compliance (Step 5)
    ftcRequirements: '',
    requiredWording: [''],
    prohibitedWording: [''],
    medicalDisclaimers: '',
    regulatoryConsiderations: '',
    approvalProcess: '',
    legalReviewRequired: false,

    // Budget (Step 6)
    totalBudget: '',
    creatorCount: '',
    perCreatorBudgetRange: '',
    usageRights: '',
    usageRightsDuration: '',
    exclusivityCompensation: '',
    revisionsIncluded: 2,
    bonusStructure: '',

    // Measurement (Step 7)
    primaryKPIs: [],
    targetMetrics: '',
    reportingFrequency: '',
    successDefinition: '',
    benchmarks: '',
  })

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const addToArray = (field) => {
    setFormData(prev => ({ ...prev, [field]: [...prev[field], ''] }))
  }

  const updateArrayItem = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }))
  }

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }))
  }

  const toggleArrayItem = (field, item) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }))
  }

  const handleNext = async () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1)
    } else {
      // Submit brief to backend
      setIsSubmitting(true)
      setError('')

      try {
        const response = await fetch('/api/campaigns', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            brandUserId: user?.id,
            brandName: user?.profile?.companyName || user?.name || 'Unknown Brand',
            name: formData.campaignTitle || 'New Campaign',
            brief: formData,
            budget: parseInt(formData.totalBudget) || 0,
          }),
        })

        const data = await response.json()

        if (!response.ok) {
          throw new Error(data.error || 'Failed to create campaign')
        }

        navigate('/brand/campaigns')
      } catch (err) {
        setError(err.message || 'Failed to create campaign. Please try again.')
        setIsSubmitting(false)
      }
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <Info className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-blue-900">Brand Context Helps Creators Succeed</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    The more detail you provide about your brand, the better creators can authentically represent you. This information helps SOFI match you with aligned creators.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="label">Brand Story <span className="text-red-500">*</span></label>
              <p className="text-sm text-gray-500 mb-2">2-3 sentences that capture your brand's positioning and mission</p>
              <textarea
                value={formData.brandStory}
                onChange={(e) => updateFormData('brandStory', e.target.value)}
                className="input min-h-[100px]"
                placeholder="e.g., We're transforming chronic disease management through connected medical devices. Our mission is to empower patients with real-time health insights that improve outcomes."
                required
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Product/Service Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.productServiceName}
                  onChange={(e) => updateFormData('productServiceName', e.target.value)}
                  className="input"
                  placeholder="e.g., HealthTrack Pro Smartwatch"
                  required
                />
              </div>
              <div>
                <label className="label">Brand Website</label>
                <div className="relative">
                  <Link className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="url"
                    value={formData.brandWebsite}
                    onChange={(e) => updateFormData('brandWebsite', e.target.value)}
                    className="input pl-10"
                    placeholder="https://yourbrand.com"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="label">Product/Service Description <span className="text-red-500">*</span></label>
              <p className="text-sm text-gray-500 mb-2">What is it? How does it work? Who is it for?</p>
              <textarea
                value={formData.productDescription}
                onChange={(e) => updateFormData('productDescription', e.target.value)}
                className="input min-h-[100px]"
                placeholder="Describe your product/service in detail. Include key features, how it works, and who benefits from it."
                required
              />
            </div>

            <div>
              <label className="label">Unique Selling Points</label>
              <p className="text-sm text-gray-500 mb-2">What makes your product/service different from competitors?</p>
              {formData.uniqueSellingPoints.map((point, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={point}
                    onChange={(e) => updateArrayItem('uniqueSellingPoints', index, e.target.value)}
                    className="input flex-1"
                    placeholder={`e.g., FDA-cleared, Clinically proven, 10x more accurate`}
                  />
                  {formData.uniqueSellingPoints.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayItem('uniqueSellingPoints', index)}
                      className="p-2 text-gray-400 hover:text-red-500"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
              {formData.uniqueSellingPoints.length < 5 && (
                <button
                  type="button"
                  onClick={() => addToArray('uniqueSellingPoints')}
                  className="text-sm text-hf-teal-600 hover:text-hf-teal-700 font-medium flex items-center"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add selling point
                </button>
              )}
            </div>

            <div>
              <label className="label">Brand Values</label>
              <p className="text-sm text-gray-500 mb-2">What does your brand stand for? (Select up to 5)</p>
              <div className="flex flex-wrap gap-2 mb-2">
                {['Innovation', 'Trust', 'Patient-First', 'Evidence-Based', 'Accessibility', 'Transparency', 'Quality', 'Compassion', 'Excellence', 'Integrity'].map(value => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => toggleArrayItem('brandValues', value)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                      formData.brandValues.includes(value)
                        ? 'bg-hf-teal-100 border-hf-teal-300 text-hf-teal-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {formData.brandValues.includes(value) && <Check className="w-3 h-3 inline mr-1" />}
                    {value}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="label">Tone of Voice <span className="text-red-500">*</span></label>
              <p className="text-sm text-gray-500 mb-2">How should creators communicate about your brand?</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {tones.map(tone => (
                  <button
                    key={tone}
                    type="button"
                    onClick={() => updateFormData('toneOfVoice', tone)}
                    className={`p-3 rounded-lg border text-sm font-medium text-left transition-colors ${
                      formData.toneOfVoice === tone
                        ? 'bg-hf-teal-50 border-hf-teal-300 text-hf-teal-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {tone}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="label">Target Audience <span className="text-red-500">*</span></label>
              <p className="text-sm text-gray-500 mb-2">Be specific: demographics, health conditions, interests, pain points</p>
              <textarea
                value={formData.targetAudience}
                onChange={(e) => updateFormData('targetAudience', e.target.value)}
                className="input min-h-[100px]"
                placeholder="e.g., Adults 35-65 managing Type 2 diabetes who are tech-savvy, actively seeking better ways to monitor their health, frustrated with traditional glucose monitoring methods, active on Instagram and YouTube for health tips."
                required
              />
            </div>

            <div>
              <label className="label">Competitor Information</label>
              <p className="text-sm text-gray-500 mb-2">Who are your main competitors? This helps creators avoid unintended comparisons.</p>
              <textarea
                value={formData.competitorInfo}
                onChange={(e) => updateFormData('competitorInfo', e.target.value)}
                className="input min-h-[80px]"
                placeholder="List main competitors and any positioning notes (optional)"
              />
            </div>

            <div>
              <label className="label">Brand Assets Available</label>
              <p className="text-sm text-gray-500 mb-2">What assets can you provide to creators?</p>
              <textarea
                value={formData.brandAssets}
                onChange={(e) => updateFormData('brandAssets', e.target.value)}
                className="input min-h-[80px]"
                placeholder="e.g., Product samples will be shipped, B-roll footage available, Brand guidelines PDF, Logo files in multiple formats, Product photography"
              />
            </div>
          </div>
        )

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <label className="label">Campaign Title <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={formData.campaignTitle}
                onChange={(e) => updateFormData('campaignTitle', e.target.value)}
                className="input"
                placeholder="e.g., Spring Wellness Launch 2026"
                required
              />
            </div>

            <div>
              <label className="label">Campaign Description <span className="text-red-500">*</span></label>
              <p className="text-sm text-gray-500 mb-2">Provide context about this campaign - what's the occasion, why now?</p>
              <textarea
                value={formData.campaignDescription}
                onChange={(e) => updateFormData('campaignDescription', e.target.value)}
                className="input min-h-[100px]"
                placeholder="e.g., Launching our new glucose monitor ahead of Diabetes Awareness Month. We want to generate buzz and educate our target audience about the benefits of continuous monitoring."
                required
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Primary Goal <span className="text-red-500">*</span></label>
                <select
                  value={formData.primaryGoal}
                  onChange={(e) => updateFormData('primaryGoal', e.target.value)}
                  className="input"
                  required
                >
                  <option value="">Select goal</option>
                  {goals.map(goal => (
                    <option key={goal} value={goal}>{goal}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Secondary Goal</label>
                <select
                  value={formData.secondaryGoal}
                  onChange={(e) => updateFormData('secondaryGoal', e.target.value)}
                  className="input"
                >
                  <option value="">Select goal (optional)</option>
                  {goals.map(goal => (
                    <option key={goal} value={goal}>{goal}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Call to Action <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.callToAction}
                  onChange={(e) => updateFormData('callToAction', e.target.value)}
                  className="input"
                  placeholder="e.g., Visit website, Use code SAVE20, Book consultation"
                  required
                />
              </div>
              <div>
                <label className="label">CTA Link/Code</label>
                <div className="relative">
                  <Link className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={formData.ctaLink}
                    onChange={(e) => updateFormData('ctaLink', e.target.value)}
                    className="input pl-10"
                    placeholder="https://yourbrand.com/promo or CREATOR20"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="label">Core Message <span className="text-red-500">*</span></label>
              <p className="text-sm text-gray-500 mb-2">The single most important takeaway for viewers</p>
              <textarea
                value={formData.coreMessage}
                onChange={(e) => updateFormData('coreMessage', e.target.value)}
                className="input min-h-[80px]"
                placeholder="e.g., Managing your health shouldn't be complicated - our device makes it simple to stay on top of your wellness goals."
                required
              />
            </div>

            <div>
              <label className="label">Key Talking Points <span className="text-red-500">*</span></label>
              <p className="text-sm text-gray-500 mb-2">3-5 specific points creators MUST cover in their content</p>
              {formData.talkingPoints.map((point, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <span className="flex items-center justify-center w-8 h-10 text-sm text-gray-400 font-medium">{index + 1}.</span>
                  <input
                    type="text"
                    value={point}
                    onChange={(e) => updateArrayItem('talkingPoints', index, e.target.value)}
                    className="input flex-1"
                    placeholder={index === 0 ? "e.g., Mention the FDA clearance" : index === 1 ? "e.g., Demonstrate how easy it is to use" : "Add another key point"}
                  />
                  {formData.talkingPoints.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayItem('talkingPoints', index)}
                      className="p-2 text-gray-400 hover:text-red-500"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
              {formData.talkingPoints.length < 5 && (
                <button
                  type="button"
                  onClick={() => addToArray('talkingPoints')}
                  className="text-sm text-hf-teal-600 hover:text-hf-teal-700 font-medium flex items-center ml-10"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add talking point
                </button>
              )}
            </div>

            <div>
              <label className="label">Key Benefits to Highlight</label>
              <p className="text-sm text-gray-500 mb-2">Product/service benefits creators should emphasize</p>
              {formData.keyBenefits.map((benefit, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={benefit}
                    onChange={(e) => updateArrayItem('keyBenefits', index, e.target.value)}
                    className="input flex-1"
                    placeholder="e.g., 24/7 continuous monitoring, No finger pricks needed, Real-time alerts"
                  />
                  {formData.keyBenefits.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayItem('keyBenefits', index)}
                      className="p-2 text-gray-400 hover:text-red-500"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
              {formData.keyBenefits.length < 5 && (
                <button
                  type="button"
                  onClick={() => addToArray('keyBenefits')}
                  className="text-sm text-hf-teal-600 hover:text-hf-teal-700 font-medium flex items-center"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add benefit
                </button>
              )}
            </div>

            <div>
              <label className="label">Proof Points & Statistics</label>
              <p className="text-sm text-gray-500 mb-2">Data or evidence creators can cite (be accurate!)</p>
              {formData.proofPoints.map((proof, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={proof}
                    onChange={(e) => updateArrayItem('proofPoints', index, e.target.value)}
                    className="input flex-1"
                    placeholder='e.g., "98% accuracy in clinical trials", "Used by 50,000+ patients"'
                  />
                  {formData.proofPoints.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayItem('proofPoints', index)}
                      className="p-2 text-gray-400 hover:text-red-500"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
              {formData.proofPoints.length < 5 && (
                <button
                  type="button"
                  onClick={() => addToArray('proofPoints')}
                  className="text-sm text-hf-teal-600 hover:text-hf-teal-700 font-medium flex items-center"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add proof point
                </button>
              )}
            </div>
          </div>
        )

      case 3:
        return (
          <div className="space-y-6">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <Calendar className="w-5 h-5 text-purple-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-purple-900">Timeline & Creator Matching</h4>
                  <p className="text-sm text-purple-700 mt-1">
                    Set your campaign timeline and specify the type of creators you're looking for. SOFI will use this to find the best matches.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Campaign Start Date <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={formData.campaignStartDate}
                  onChange={(e) => updateFormData('campaignStartDate', e.target.value)}
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="label">Campaign End Date <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={formData.campaignEndDate}
                  onChange={(e) => updateFormData('campaignEndDate', e.target.value)}
                  className="input"
                  required
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Content Deadline <span className="text-red-500">*</span></label>
                <p className="text-sm text-gray-500 mb-2">When must all content be submitted for review?</p>
                <input
                  type="date"
                  value={formData.contentDeadline}
                  onChange={(e) => updateFormData('contentDeadline', e.target.value)}
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="label">Posting Window</label>
                <p className="text-sm text-gray-500 mb-2">When should content go live?</p>
                <input
                  type="text"
                  value={formData.postingWindow}
                  onChange={(e) => updateFormData('postingWindow', e.target.value)}
                  className="input"
                  placeholder="e.g., Feb 1-15, or 'Within 48hrs of approval'"
                />
              </div>
            </div>

            <div>
              <label className="label">Preferred Creator Tiers</label>
              <p className="text-sm text-gray-500 mb-2">Select the audience sizes you're targeting</p>
              <div className="flex flex-wrap gap-2">
                {creatorTiers.map(tier => (
                  <button
                    key={tier}
                    type="button"
                    onClick={() => toggleArrayItem('preferredCreatorTiers', tier)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      formData.preferredCreatorTiers.includes(tier)
                        ? 'bg-hf-teal-100 border-hf-teal-300 text-hf-teal-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {formData.preferredCreatorTiers.includes(tier) && <Check className="w-4 h-4 inline mr-1" />}
                    {tier}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="label">Preferred Creator Specialties</label>
              <p className="text-sm text-gray-500 mb-2">What type of healthcare professionals/advocates?</p>
              <div className="flex flex-wrap gap-2">
                {creatorSpecialties.map(specialty => (
                  <button
                    key={specialty}
                    type="button"
                    onClick={() => toggleArrayItem('preferredSpecialties', specialty)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      formData.preferredSpecialties.includes(specialty)
                        ? 'bg-hf-teal-100 border-hf-teal-300 text-hf-teal-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {formData.preferredSpecialties.includes(specialty) && <Check className="w-4 h-4 inline mr-1" />}
                    {specialty}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="label">Creator Personality & Style</label>
              <p className="text-sm text-gray-500 mb-2">Describe the ideal creator personality for this campaign</p>
              <textarea
                value={formData.creatorPersonality}
                onChange={(e) => updateFormData('creatorPersonality', e.target.value)}
                className="input min-h-[80px]"
                placeholder="e.g., Warm and approachable, professionally credentialed but relatable, good at explaining complex topics simply, active engagement with their community"
              />
            </div>

            <div>
              <label className="label">Geographic Requirements</label>
              <input
                type="text"
                value={formData.geographicRequirements}
                onChange={(e) => updateFormData('geographicRequirements', e.target.value)}
                className="input"
                placeholder="e.g., US-based only, English-speaking, No restrictions"
              />
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <label className="label mb-0">Exclusivity Required?</label>
                <button
                  type="button"
                  onClick={() => updateFormData('exclusivityRequired', !formData.exclusivityRequired)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formData.exclusivityRequired ? 'bg-hf-teal-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.exclusivityRequired ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <p className="text-sm text-gray-500 mb-3">Prevent creators from working with competitors during and after the campaign</p>

              {formData.exclusivityRequired && (
                <div>
                  <label className="label text-sm">Exclusivity Duration</label>
                  <input
                    type="text"
                    value={formData.exclusivityDuration}
                    onChange={(e) => updateFormData('exclusivityDuration', e.target.value)}
                    className="input"
                    placeholder="e.g., 30 days after posting, Duration of campaign + 60 days"
                  />
                </div>
              )}
            </div>
          </div>
        )

      case 4:
        return (
          <div className="space-y-6">
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <Video className="w-5 h-5 text-teal-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-teal-900">Detailed Deliverable Specifications</h4>
                  <p className="text-sm text-teal-700 mt-1">
                    Clear deliverable requirements ensure creators know exactly what to produce. The more specific you are, the fewer revisions needed.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="label">Platforms <span className="text-red-500">*</span></label>
              <p className="text-sm text-gray-500 mb-2">Select all platforms where content should be posted</p>
              <div className="flex flex-wrap gap-2">
                {platforms.map(platform => (
                  <button
                    key={platform}
                    type="button"
                    onClick={() => toggleArrayItem('platforms', platform)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      formData.platforms.includes(platform)
                        ? 'bg-hf-teal-100 border-hf-teal-300 text-hf-teal-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {formData.platforms.includes(platform) && <Check className="w-4 h-4 inline mr-1" />}
                    {platform}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="label">Deliverable Types <span className="text-red-500">*</span></label>
              <p className="text-sm text-gray-500 mb-2">What type of content do you need?</p>
              <div className="flex flex-wrap gap-2">
                {deliverableTypes.map(type => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => toggleArrayItem('deliverableTypes', type)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      formData.deliverableTypes.includes(type)
                        ? 'bg-hf-teal-100 border-hf-teal-300 text-hf-teal-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {formData.deliverableTypes.includes(type) && <Check className="w-4 h-4 inline mr-1" />}
                    {type}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="label">Content Formats</label>
              <p className="text-sm text-gray-500 mb-2">Preferred creative styles/formats</p>
              <div className="flex flex-wrap gap-2">
                {contentFormats.map(format => (
                  <button
                    key={format}
                    type="button"
                    onClick={() => toggleArrayItem('contentFormats', format)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      formData.contentFormats.includes(format)
                        ? 'bg-hf-teal-100 border-hf-teal-300 text-hf-teal-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {formData.contentFormats.includes(format) && <Check className="w-4 h-4 inline mr-1" />}
                    {format}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="label">Video Length Requirements</label>
              <input
                type="text"
                value={formData.videoLength}
                onChange={(e) => updateFormData('videoLength', e.target.value)}
                className="input"
                placeholder="e.g., Reels: 30-60 seconds, Long-form: 5-10 minutes"
              />
            </div>

            <div>
              <label className="label">Creative Direction <span className="text-red-500">*</span></label>
              <p className="text-sm text-gray-500 mb-2">Describe the mood, pacing, visual style you want</p>
              <textarea
                value={formData.creativeDirection}
                onChange={(e) => updateFormData('creativeDirection', e.target.value)}
                className="input min-h-[100px]"
                placeholder="e.g., Bright and energetic, filmed in a clinical setting or home environment. Start with a hook about a common pain point. Include a personal anecdote if applicable. End with clear CTA."
                required
              />
            </div>

            <div>
              <label className="label">Mood & Aesthetic Descriptors</label>
              <input
                type="text"
                value={formData.moodDescriptors}
                onChange={(e) => updateFormData('moodDescriptors', e.target.value)}
                className="input"
                placeholder="e.g., Clean, professional, warm lighting, approachable, modern"
              />
            </div>

            <div>
              <label className="label">Caption Guidelines</label>
              <p className="text-sm text-gray-500 mb-2">Requirements for written post captions</p>
              <textarea
                value={formData.captionGuidelines}
                onChange={(e) => updateFormData('captionGuidelines', e.target.value)}
                className="input min-h-[80px]"
                placeholder="e.g., Include personal story element, mention the product naturally, add educational value, include CTA, keep under 2200 characters for Instagram"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Required Hashtags</label>
                {formData.hashtagRequirements.map((tag, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={tag}
                      onChange={(e) => updateArrayItem('hashtagRequirements', index, e.target.value)}
                      className="input flex-1"
                      placeholder="#YourBrandHashtag"
                    />
                    {formData.hashtagRequirements.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeArrayItem('hashtagRequirements', index)}
                        className="p-2 text-gray-400 hover:text-red-500"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addToArray('hashtagRequirements')}
                  className="text-sm text-hf-teal-600 hover:text-hf-teal-700 font-medium flex items-center"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add hashtag
                </button>
              </div>
              <div>
                <label className="label">Required Mentions/Tags</label>
                {formData.mentionRequirements.map((mention, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={mention}
                      onChange={(e) => updateArrayItem('mentionRequirements', index, e.target.value)}
                      className="input flex-1"
                      placeholder="@YourBrandHandle"
                    />
                    {formData.mentionRequirements.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeArrayItem('mentionRequirements', index)}
                        className="p-2 text-gray-400 hover:text-red-500"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addToArray('mentionRequirements')}
                  className="text-sm text-hf-teal-600 hover:text-hf-teal-700 font-medium flex items-center"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add mention
                </button>
              </div>
            </div>

            <div>
              <label className="label">Mandatory Visual/Audio Inclusions</label>
              <p className="text-sm text-gray-500 mb-2">Elements that MUST appear in the content</p>
              {formData.mandatoryInclusions.map((item, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => updateArrayItem('mandatoryInclusions', index, e.target.value)}
                    className="input flex-1"
                    placeholder="e.g., Product must be shown in use, Brand name spoken, Link in bio callout"
                  />
                  {formData.mandatoryInclusions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayItem('mandatoryInclusions', index)}
                      className="p-2 text-gray-400 hover:text-red-500"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => addToArray('mandatoryInclusions')}
                className="text-sm text-hf-teal-600 hover:text-hf-teal-700 font-medium flex items-center"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add inclusion
              </button>
            </div>

            <div>
              <label className="label flex items-center text-amber-700">
                <AlertCircle className="w-4 h-4 mr-2" />
                Content Restrictions
              </label>
              <p className="text-sm text-gray-500 mb-2">What should creators absolutely avoid?</p>
              {formData.restrictions.map((item, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => updateArrayItem('restrictions', index, e.target.value)}
                    className="input flex-1"
                    placeholder="e.g., No competitor mentions, No unsubstantiated claims, No children in content"
                  />
                  {formData.restrictions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayItem('restrictions', index)}
                      className="p-2 text-gray-400 hover:text-red-500"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => addToArray('restrictions')}
                className="text-sm text-hf-teal-600 hover:text-hf-teal-700 font-medium flex items-center"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add restriction
              </button>
            </div>

            <div>
              <label className="label">Reference Examples</label>
              <p className="text-sm text-gray-500 mb-2">Links to content that represents the style you want (competitor or non-competitor)</p>
              {formData.referenceLinks.map((link, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <div className="relative flex-1">
                    <Link className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="url"
                      value={link}
                      onChange={(e) => updateArrayItem('referenceLinks', index, e.target.value)}
                      className="input pl-10"
                      placeholder="https://instagram.com/p/example or https://tiktok.com/@creator/video/123"
                    />
                  </div>
                  {formData.referenceLinks.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayItem('referenceLinks', index)}
                      className="p-2 text-gray-400 hover:text-red-500"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => addToArray('referenceLinks')}
                className="text-sm text-hf-teal-600 hover:text-hf-teal-700 font-medium flex items-center"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add reference link
              </button>
            </div>
          </div>
        )

      case 5:
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <Shield className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-blue-900">Healthcare Compliance is Critical</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    SOFI automatically enforces FTC disclosure requirements and reviews all content for compliance. The details below help us ensure content meets healthcare marketing regulations and your brand's legal requirements.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="label">FTC Disclosure Requirements</label>
              <p className="text-sm text-gray-500 mb-2">Any specific requirements beyond standard #ad or #sponsored disclosure?</p>
              <textarea
                value={formData.ftcRequirements}
                onChange={(e) => updateFormData('ftcRequirements', e.target.value)}
                className="input min-h-[80px]"
                placeholder="e.g., Material connection must be disclosed in first 3 seconds of video, Disclosure must be verbal AND written, Must use specific phrase 'Paid partnership with [Brand]'"
              />
            </div>

            <div>
              <label className="label">Medical Disclaimers Required</label>
              <p className="text-sm text-gray-500 mb-2">Healthcare-specific disclaimers that must appear</p>
              <textarea
                value={formData.medicalDisclaimers}
                onChange={(e) => updateFormData('medicalDisclaimers', e.target.value)}
                className="input min-h-[100px]"
                placeholder='e.g., "This is not medical advice. Always consult your healthcare provider before starting any new treatment or device." or "Individual results may vary. This product is not intended to diagnose, treat, cure, or prevent any disease."'
              />
            </div>

            <div>
              <label className="label">Regulatory Considerations</label>
              <p className="text-sm text-gray-500 mb-2">FDA, HIPAA, or other regulatory requirements specific to your product</p>
              <textarea
                value={formData.regulatoryConsiderations}
                onChange={(e) => updateFormData('regulatoryConsiderations', e.target.value)}
                className="input min-h-[100px]"
                placeholder="e.g., FDA-cleared for [specific use] only - creators cannot claim off-label uses. Cannot show patient data or identifiable health information. Must reference only approved indications."
              />
            </div>

            <div>
              <label className="label">Required Wording/Phrases</label>
              <p className="text-sm text-gray-500 mb-2">Exact phrases or disclosures that MUST be included verbatim</p>
              {formData.requiredWording.map((item, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => updateArrayItem('requiredWording', index, e.target.value)}
                    className="input flex-1"
                    placeholder='e.g., "Talk to your doctor to see if [Product] is right for you"'
                  />
                  {formData.requiredWording.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayItem('requiredWording', index)}
                      className="p-2 text-gray-400 hover:text-red-500"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => addToArray('requiredWording')}
                className="text-sm text-hf-teal-600 hover:text-hf-teal-700 font-medium flex items-center"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add required phrase
              </button>
            </div>

            <div>
              <label className="label flex items-center text-red-700">
                <AlertCircle className="w-4 h-4 mr-2" />
                Prohibited Claims & Wording
              </label>
              <p className="text-sm text-gray-500 mb-2">Words, phrases, or claims that must NEVER appear</p>
              {formData.prohibitedWording.map((item, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => updateArrayItem('prohibitedWording', index, e.target.value)}
                    className="input flex-1"
                    placeholder='e.g., "Cure", "Miracle", "Guaranteed", "FDA approved" (if only cleared), "Safe for everyone"'
                  />
                  {formData.prohibitedWording.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayItem('prohibitedWording', index)}
                      className="p-2 text-gray-400 hover:text-red-500"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => addToArray('prohibitedWording')}
                className="text-sm text-hf-teal-600 hover:text-hf-teal-700 font-medium flex items-center"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add prohibited phrase
              </button>
            </div>

            <div>
              <label className="label">Approval Process Notes</label>
              <p className="text-sm text-gray-500 mb-2">Any internal approval requirements beyond SOFI review?</p>
              <textarea
                value={formData.approvalProcess}
                onChange={(e) => updateFormData('approvalProcess', e.target.value)}
                className="input min-h-[80px]"
                placeholder="e.g., All content must be reviewed by our Medical Affairs team before final approval. Legal sign-off required for any content mentioning clinical studies."
              />
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <label className="label mb-0">Legal Review Required?</label>
                <button
                  type="button"
                  onClick={() => updateFormData('legalReviewRequired', !formData.legalReviewRequired)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formData.legalReviewRequired ? 'bg-hf-teal-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.legalReviewRequired ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <p className="text-sm text-gray-500">Toggle on if your legal team must review and approve all content before posting</p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-amber-900">Common Healthcare Marketing Pitfalls</h4>
                  <ul className="text-sm text-amber-700 mt-2 space-y-1">
                    <li>• Avoid absolute claims ("always works", "100% effective")</li>
                    <li>• Don't promise specific results or timelines</li>
                    <li>• Never encourage stopping prescribed treatments</li>
                    <li>• Don't share or imply access to patient testimonials without proper consent</li>
                    <li>• Avoid before/after claims without proper substantiation</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )

      case 6:
        return (
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Total Campaign Budget <span className="text-red-500">*</span></label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <input
                    type="number"
                    value={formData.totalBudget}
                    onChange={(e) => updateFormData('totalBudget', e.target.value)}
                    className="input pl-8"
                    placeholder="50,000"
                    required
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Total budget including creator fees and SOFI platform fee</p>
              </div>
              <div>
                <label className="label">Number of Creators <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  value={formData.creatorCount}
                  onChange={(e) => updateFormData('creatorCount', e.target.value)}
                  className="input"
                  placeholder="5"
                  min="1"
                  required
                />
              </div>
            </div>

            <div>
              <label className="label">Per-Creator Budget Range</label>
              <p className="text-sm text-gray-500 mb-2">What's your budget range per creator? (Helps with matching)</p>
              <input
                type="text"
                value={formData.perCreatorBudgetRange}
                onChange={(e) => updateFormData('perCreatorBudgetRange', e.target.value)}
                className="input"
                placeholder="e.g., $3,000 - $8,000 per creator depending on reach"
              />
            </div>

            <div>
              <label className="label">Revisions Included</label>
              <p className="text-sm text-gray-500 mb-2">Number of revision rounds per deliverable</p>
              <div className="flex items-center gap-4">
                {[1, 2, 3].map(num => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => updateFormData('revisionsIncluded', num)}
                    className={`w-12 h-12 rounded-lg border text-lg font-medium transition-colors ${
                      formData.revisionsIncluded === num
                        ? 'bg-hf-teal-100 border-hf-teal-300 text-hf-teal-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                <DollarSign className="w-4 h-4 mr-2" />
                Payment Milestones
              </h4>
              <p className="text-sm text-gray-500 mb-4">SOFI uses escrow to protect both parties. Funds are released at these milestones:</p>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Draft Approved</span>
                  <span className="font-medium">50%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Final Approved</span>
                  <span className="font-medium">25%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Content Posted</span>
                  <span className="font-medium">25%</span>
                </div>
              </div>
            </div>

            <div>
              <label className="label">Content Usage Rights <span className="text-red-500">*</span></label>
              <p className="text-sm text-gray-500 mb-2">How will you use the creator's content beyond their organic post?</p>
              <select
                value={formData.usageRights}
                onChange={(e) => updateFormData('usageRights', e.target.value)}
                className="input"
                required
              >
                <option value="">Select usage rights</option>
                <option value="organic_only">Organic Only - Creator posts only, no brand repurposing</option>
                <option value="organic_repost">Organic + Repost - Brand can repost on owned channels</option>
                <option value="paid_media">Paid Media - Brand can use in paid advertising (whitelisting)</option>
                <option value="full_rights">Full Rights - Unlimited usage including TV, print, OOH</option>
              </select>
            </div>

            <div>
              <label className="label">Usage Rights Duration</label>
              <input
                type="text"
                value={formData.usageRightsDuration}
                onChange={(e) => updateFormData('usageRightsDuration', e.target.value)}
                className="input"
                placeholder="e.g., 12 months from posting date, Perpetual, Duration of campaign"
              />
            </div>

            {formData.exclusivityRequired && (
              <div>
                <label className="label">Exclusivity Compensation</label>
                <p className="text-sm text-gray-500 mb-2">Additional compensation for exclusivity (typically 10-30% premium)</p>
                <input
                  type="text"
                  value={formData.exclusivityCompensation}
                  onChange={(e) => updateFormData('exclusivityCompensation', e.target.value)}
                  className="input"
                  placeholder="e.g., 20% premium on base rate, $500 flat fee"
                />
              </div>
            )}

            <div>
              <label className="label">Performance Bonus Structure</label>
              <p className="text-sm text-gray-500 mb-2">Optional bonus for exceeding performance targets</p>
              <textarea
                value={formData.bonusStructure}
                onChange={(e) => updateFormData('bonusStructure', e.target.value)}
                className="input min-h-[80px]"
                placeholder="e.g., 10% bonus if content reaches 1M+ views, $200 bonus per 100 conversions tracked via unique link"
              />
            </div>
          </div>
        )

      case 7:
        return (
          <div className="space-y-6">
            <div>
              <label className="label">Primary KPIs <span className="text-red-500">*</span></label>
              <p className="text-sm text-gray-500 mb-2">How will you measure campaign success?</p>
              <div className="flex flex-wrap gap-2">
                {['Reach', 'Impressions', 'Engagement Rate', 'Clicks', 'Conversions', 'Sales', 'Sign-ups', 'App Downloads', 'Brand Awareness', 'Sentiment', 'Video Views', 'Cost per Acquisition'].map(kpi => (
                  <button
                    key={kpi}
                    type="button"
                    onClick={() => toggleArrayItem('primaryKPIs', kpi)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      formData.primaryKPIs.includes(kpi)
                        ? 'bg-hf-teal-100 border-hf-teal-300 text-hf-teal-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {formData.primaryKPIs.includes(kpi) && <Check className="w-4 h-4 inline mr-1" />}
                    {kpi}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="label">Target Metrics</label>
              <p className="text-sm text-gray-500 mb-2">Specific numeric targets you're aiming to achieve</p>
              <textarea
                value={formData.targetMetrics}
                onChange={(e) => updateFormData('targetMetrics', e.target.value)}
                className="input min-h-[100px]"
                placeholder="e.g.,
• Total Reach: 2M+ impressions
• Engagement Rate: 4%+ average across all posts
• Website Clicks: 5,000+ tracked clicks
• Conversions: 200+ sign-ups via unique tracking links
• Cost per Acquisition: Under $50"
              />
            </div>

            <div>
              <label className="label">Industry Benchmarks</label>
              <p className="text-sm text-gray-500 mb-2">What are typical results for similar campaigns in your industry?</p>
              <textarea
                value={formData.benchmarks}
                onChange={(e) => updateFormData('benchmarks', e.target.value)}
                className="input min-h-[80px]"
                placeholder="e.g., Previous campaign achieved 1.5M reach with 3.2% engagement. Industry average for healthcare is 2.8% engagement on Instagram."
              />
            </div>

            <div>
              <label className="label">Reporting Frequency</label>
              <select
                value={formData.reportingFrequency}
                onChange={(e) => updateFormData('reportingFrequency', e.target.value)}
                className="input"
              >
                <option value="">Select reporting frequency</option>
                <option value="realtime">Real-time Dashboard Access</option>
                <option value="weekly">Weekly Summary Reports</option>
                <option value="biweekly">Bi-weekly Reports</option>
                <option value="monthly">Monthly Reports</option>
                <option value="campaign_end">End of Campaign Report Only</option>
              </select>
            </div>

            <div>
              <label className="label">Success Definition <span className="text-red-500">*</span></label>
              <p className="text-sm text-gray-500 mb-2">What specific outcomes would make this campaign a success for your team?</p>
              <textarea
                value={formData.successDefinition}
                onChange={(e) => updateFormData('successDefinition', e.target.value)}
                className="input min-h-[120px]"
                placeholder="e.g., This campaign will be considered successful if we:
1. Achieve 2M+ total reach across all creator content
2. Maintain 4%+ average engagement rate
3. Generate 500+ qualified leads via tracking links
4. See positive sentiment in comments (80%+ positive)
5. Stay within budget with positive ROI"
                required
              />
            </div>

            <div className="bg-gradient-to-r from-hf-teal-50 to-teal-50 rounded-lg p-4 border border-hf-teal-100">
              <div className="flex items-start">
                <Sparkles className="w-5 h-5 text-hf-teal-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-gray-900">AI-Powered Analytics & Insights</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Once your campaign launches, SOFI will automatically collect metrics from all platforms and provide:
                  </p>
                  <ul className="text-sm text-gray-600 mt-2 space-y-1">
                    <li>• Real-time performance dashboards</li>
                    <li>• Automated benchmark comparisons</li>
                    <li>• AI-generated optimization recommendations</li>
                    <li>• Sentiment analysis on comments</li>
                    <li>• ROI calculations and attribution</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center mb-8">
        <button
          onClick={() => navigate('/brand/campaigns')}
          className="p-2 text-gray-400 hover:text-gray-600 mr-4"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Campaign Brief</h1>
          <p className="text-gray-500">Complete the brief to start matching with creators</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="card mb-8">
        <div className="flex items-center justify-between overflow-x-auto pb-2">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <button
                onClick={() => setCurrentStep(step.id)}
                className={`flex flex-col items-center min-w-[80px] ${
                  currentStep === step.id ? 'text-hf-teal-600' : currentStep > step.id ? 'text-green-600' : 'text-gray-400'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                  currentStep === step.id
                    ? 'bg-hf-teal-100'
                    : currentStep > step.id
                    ? 'bg-green-100'
                    : 'bg-gray-100'
                }`}>
                  {currentStep > step.id ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <step.icon className="w-5 h-5" />
                  )}
                </div>
                <span className="text-xs font-medium text-center">{step.name}</span>
              </button>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${currentStep > step.id ? 'bg-green-300' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Form Content */}
      <div className="card mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
          {React.createElement(steps[currentStep - 1].icon, { className: 'w-5 h-5 mr-2 text-hf-teal-600' })}
          {steps[currentStep - 1].name}
        </h2>
        {renderStepContent()}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <button
          onClick={handleBack}
          disabled={currentStep === 1}
          className="btn-secondary disabled:opacity-50"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>
        <button onClick={handleNext} disabled={isSubmitting} className="btn-primary disabled:opacity-50">
          {isSubmitting ? 'Creating Campaign...' : currentStep === steps.length ? 'Submit Brief' : 'Continue'}
          {!isSubmitting && <ArrowRight className="w-4 h-4 ml-2" />}
        </button>
      </div>

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}
    </div>
  )
}

export default BrandBriefBuilder
